import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { METHODS, SERVICES } from '../../api/constants';
import { commonMoodlePayload } from '../../config';
import { makeRequest } from '../../api/api';
import { MOODLE_BACKEND_ROUTES } from '../../config/apiRoutes';
import { toast } from 'react-toastify';

export const getTags = createAsyncThunk(
  'learningHub/getTag',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_RESOURCE_TAGS,
          wstoken: thunkApi.getState().auth.token
        }
      );
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const postBookMark = createAsyncThunk(
  'learningHub/postBookmark',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param?.data,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.POST_BOOKAMRK_ARTICAL,
          wstoken: thunkApi.getState().auth.token
        }
      );
      if (response?.status === 200 && Number(response?.data?.status)) {
        if (param?.data?.saved) {
          toast.success('Saved for reading later', {
            closeOnClick: true,
            autoClose: 5000
          });
        }
        if (param?.source) {
          thunkApi.dispatch(setBookmark({ lrid: param?.data?.lrid }));
        } else {
          thunkApi.dispatch(handleBookMark({ lrid: param?.data?.lrid }));
        }
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const postMarkasRead = createAsyncThunk(
  'learningHub/postMarkasRead',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.MARK_ARTICAL_READ,
          wstoken: thunkApi.getState().auth.token
        }
      );
      if (response?.status === 200 && Number(response?.data?.status)) {
        thunkApi.dispatch(handleRead({ lrid: param?.lrid }));
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const getResource = createAsyncThunk(
  'learningHub/getResource',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_RESOURCE_DATA,
          wstoken: thunkApi.getState().auth.token
        }
      );
      if (response?.data?.errorcode || response?.data?.exception) {
        thunkApi.rejectWithValue({});
        return;
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const getSingleResource = createAsyncThunk(
  'learningHub/getSingleResource',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_SINGLE_RESOURCE_DATA,
          wstoken: thunkApi.getState().auth.token
        }
      );
      if (response?.data?.errorcode || response?.data?.exception) {
        thunkApi.rejectWithValue({});
        return;
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const learningHub = createSlice({
  name: 'learningHub',
  initialState: {
    loading: false,
    tagLoading: false,
    tagData: [],
    resourceData: {},
    singleResourceData: {},
    resourceLoading: false,
    bookmarkLoading: false
  },
  reducers: {
    handleBookMark: (state, action) => {
      state.resourceData = {
        learningresources: state.resourceData?.learningresources.map((val) => {
          if (val?.id === action?.payload?.lrid) {
            return { ...val, saved: Number(!val?.saved) };
          }
          return val;
        })
      };
    },
    setBookmark: (state) => {
      if (Object.keys(state.singleResourceData)?.length) {
        state.singleResourceData = {
          ...state.singleResourceData,
          saved: Number(!state.singleResourceData?.saved)
        };
      }
    },
    resetData: (state) => {
      state.singleResourceData = {};
    },
    handleRead: (state, action) => {
      state.resourceData = {
        learningresources: state.resourceData?.learningresources.map((val) => {
          if (val?.id === action?.payload?.lrid) {
            return { ...val, read: Number(!val?.isread) };
          }
          return val;
        })
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getTags.pending, (state) => {
      state.tagLoading = true;
    });
    builder.addCase(getTags.fulfilled, (state, action) => {
      state.tagLoading = false;
      state.tagData = action?.payload || [];
    });
    builder.addCase(getTags.rejected, (state) => {
      state.tagLoading = false;
      state.tagData = [];
    });
    builder.addCase(getResource.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getResource.fulfilled, (state, action) => {
      state.loading = false;
      state.resourceData = action?.payload || {};
    });
    builder.addCase(getResource.rejected, (state) => {
      state.loading = false;
      state.resourceData = {};
    });
    builder.addCase(getSingleResource.pending, (state) => {
      state.resourceLoading = true;
    });
    builder.addCase(getSingleResource.fulfilled, (state, action) => {
      state.resourceLoading = false;
      state.singleResourceData = action?.payload || {};
    });
    builder.addCase(getSingleResource.rejected, (state) => {
      state.resourceLoading = false;
      state.singleResourceData = {};
    });
    builder.addCase(postBookMark.pending, (state) => {
      state.bookmarkLoading = true;
    });
    builder.addCase(postBookMark.fulfilled, (state, action) => {
      // need update the main state based on the id for bookmark as true
      state.bookmarkLoading = false;
    });
    builder.addCase(postBookMark.rejected, (state) => {
      state.bookmarkLoading = false;
    });
    builder.addCase(postMarkasRead.fulfilled, (state, action) => {
      // need update the main state based on the id for read as true
    });
  }
});

export const { handleBookMark, handleRead, resetData, setBookmark } =
  learningHub.actions;

export default learningHub.reducer;
